import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import userViewGuard from "~/hooks/useViewPermssionsCheck";
import { useAllClients } from "~/modules/client/api/client/clientQueries";
import { clientContactPersonQueries } from "~/modules/client/api/clientContactPerson/clientContactPersonQueries";
import { useAllCompanies } from "~/modules/client/api/company/companyQueries.ts";
import { useAllSalesFunnels } from "~/modules/client/api/salesFunnel/salesFunnelQueries";
import { useAllSalesTypes } from "~/modules/client/api/salesType/salesTypeQueries";
import ContactDetailsView from "~/modules/client/components/contact/ContactDetailsView";
import { useAllLanguages } from "~/modules/country/api/language/languageQueries";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";
import { getGenderDisplayNameById } from "~/modules/user/utils/genderUtils.ts";
import LoadingPage from "~/pages/LoadingPage";
import { PermissionNames } from "~/types/entityNames.ts";
import getOneOfCollection from "~/utils/getOneOfCollection";

const ContactDetailsPage: React.FC = () => {
	const guard = userViewGuard(PermissionNames.CanManageProjects);
	if (guard) return guard;

	const { contactId } = useParams();
	if (undefined === contactId) return <div>error</div>;

	const { data, options: { loadingPercent } } = useAggregatedDataV2({
		contactData: useQuery(clientContactPersonQueries.byId(contactId)),
		clients: useAllClients(),
		companies: useAllCompanies(),
		languages: useAllLanguages(),
		salesFunnels: useAllSalesFunnels(),
		salesTypes: useAllSalesTypes(),
		users: useAllUsers(),
	});

	const { contactData, clients, companies, languages, salesFunnels, salesTypes, users } = data;

	const client = useMemo(() => {
		if (clients && contactData) {
			return clients.find(c => c.id === contactData.clientId);
		}
		return undefined;
	}, [clients, contactData]);

	const company = useMemo(() => {
		if (companies && client) {
			return companies.find(company => company.id === client.companyId);
		}
		return undefined;
	}, [companies, client]);

	if (loadingPercent < 100) return <LoadingPage pcent={loadingPercent} />;

	const genderName = getGenderDisplayNameById(contactData.genderId);
	const languageDisplayName = getOneOfCollection(languages, contactData.languageId, "displayName");
	const salesFunnelDisplayName = getOneOfCollection(salesFunnels, contactData.salesFunnelId, "displayName");
	const salesTypeDisplayName = getOneOfCollection(salesTypes, contactData.salesTypeId, "displayName");
	const managerFullName = getOneOfCollection(users, contactData.managedBy, "fullName");
	return (
		<ContactDetailsView
			clientContactPersonData={contactData}
			client={client!}
			company={company!}
			additionalData={{
				genderName,
				languageDisplayName,
				managerFullName,
				salesFunnelDisplayName,
				salesTypeDisplayName,
			}}
		/>
	);
};

export default ContactDetailsPage;
